import { useState } from "react";
import { Archive, ArchiveRestore, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { TenantRead } from "@harmonyso/tenants-sdk";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useArchiveTenant,
  useUnarchiveTenant,
} from "@/hooks/use-tenant-archive";

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    if (response?.data?.detail) return response.data.detail;
  }
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "Unknown error occurred";
};

interface ArchiveTenantDialogProps {
  tenant: TenantRead;
  isArchived: boolean;
  trigger?: React.ReactNode;
}

export function ArchiveTenantDialog({
  tenant,
  isArchived,
  trigger,
}: ArchiveTenantDialogProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const archiveTenant = useArchiveTenant(tenant.id);
  const unarchiveTenant = useUnarchiveTenant(tenant.id);
  const isPending = archiveTenant.isPending || unarchiveTenant.isPending;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) setReason("");
  };

  const handleArchive = () => {
    archiveTenant.mutate(
      { reason: reason.trim() || null },
      {
        onSuccess: (status) => {
          handleOpenChange(false);
          toast.success("Tenant archived", {
            description: `Disabled ${String(
              status.credentials_disabled,
            )} integration credential(s).`,
          });
        },
        onError: (error: unknown) => {
          toast.error("Failed to archive tenant", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleUnarchive = () => {
    unarchiveTenant.mutate(undefined, {
      onSuccess: () => {
        handleOpenChange(false);
        toast.success("Tenant unarchived", {
          description: "Integrations restored to their previous state.",
        });
      },
      onError: (error: unknown) => {
        toast.error("Failed to unarchive tenant", {
          description: getErrorMessage(error),
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            {isArchived ? (
              <ArchiveRestore className="mr-1 h-3 w-3" />
            ) : (
              <Archive className="mr-1 h-3 w-3" />
            )}
            {isArchived ? "Unarchive" : "Archive"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isArchived ? (
              <ArchiveRestore className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            )}
            {isArchived ? "Unarchive Tenant" : "Archive Tenant"}
          </DialogTitle>
          <DialogDescription>
            {isArchived
              ? "Re-enable this tenant's integrations, restoring each one to the state it was in before archiving."
              : "Temporarily disable ALL of this tenant's integrations (sync, collectors, outbound sync and inbound webhooks). This is reversible - unarchiving restores each integration's previous state."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <div className="bg-muted/50 rounded-lg border p-4">
            <h4 className="text-lg font-semibold">{tenant.company}</h4>
            <div className="text-muted-foreground mt-1 text-sm">
              Tenant ID: <span className="font-mono">{tenant.id}</span>
            </div>
          </div>
        </div>

        {!isArchived && (
          <div className="space-y-2">
            <Label htmlFor="archiveReason" className="text-sm font-medium">
              Reason (optional)
            </Label>
            <Textarea
              id="archiveReason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder="Why is this tenant being archived?"
              maxLength={1024}
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={isArchived ? handleUnarchive : handleArchive}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isArchived ? "Unarchiving..." : "Archiving..."}
              </>
            ) : isArchived ? (
              "Unarchive Tenant"
            ) : (
              "Archive Tenant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
